import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import { colors, typography } from "../../theme/designSystem";
import { getDefaultInputLabelProps, getDefaultTextFieldSx } from "../../theme/textFieldStyles";
import { chatService, ChatContact, ChatGroup } from "../../services/chatService";

// Novo grupo do chat (RN-551h): nome + pessoas da lista de contatos (quem tem a permissão de
// chat nesta unidade). Quem cria entra sozinho, como administrador. Teto de 50 pessoas no total.

const MAX_MEMBERS = 49;
const TITLE_MAX = 60;

const initials = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");

const PERSON_COLORS = ["#5C7CFA", "#845EF7", "#F76707", "#E64980", "#0CA678", "#F59F00", "#1098AD", "#7048E8"];
const personColor = (id: string): string => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PERSON_COLORS[h % PERSON_COLORS.length];
};

interface Props {
  open: boolean;
  contacts: ChatContact[];
  photos: Record<string, string>;
  onClose: () => void;
  onCreated: (group: ChatGroup) => void;
}

const NewGroupModal: React.FC<Props> = ({ open, contacts, photos, onClose, onCreated }) => {
  const [title, setTitle] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle("");
      setSearch("");
      setSelected([]);
      setError(null);
    }
  }, [open]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => !q || c.name.toLowerCase().includes(q) || (c.role ?? "").toLowerCase().includes(q));
  }, [contacts, search]);

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= MAX_MEMBERS ? prev : [...prev, id]));

  const create = async () => {
    const name = title.trim();
    if (!name) {
      setError("Dê um nome ao grupo.");
      return;
    }
    if (selected.length === 0) {
      setError("Escolha pelo menos uma pessoa.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const group = await chatService.createGroup(name, selected);
      onCreated(group);
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { description?: string } } })?.response?.data;
      setError(data?.description || "Não foi possível criar o grupo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}>
      <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.5rem" }}>
        <Typography component="span" sx={{ fontSize: "1.2rem", fontWeight: typography.fontWeight.semibold }}>Novo grupo</Typography>
        <IconButton onClick={onClose} disabled={saving} sx={{ color: colors.white, padding: "0.25rem" }} aria-label="Fechar"><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "1.25rem 1.5rem 0 !important", display: "flex", flexDirection: "column", gap: 1.5 }}>
        <TextField
          size="small"
          label="Nome do grupo"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex.: Recepção, Equipe de fono"
          inputProps={{ maxLength: TITLE_MAX }}
          helperText={`${title.length}/${TITLE_MAX}`}
          InputLabelProps={getDefaultInputLabelProps()}
          sx={getDefaultTextFieldSx()}
          autoFocus
          fullWidth
        />
        <Box>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 0.75 }}>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: colors.textPrimary }}>Pessoas</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary }}>
              {selected.length} {selected.length === 1 ? "escolhida" : "escolhidas"} · até {MAX_MEMBERS} além de você
            </Typography>
          </Box>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar pessoa"
            size="small"
            fullWidth
            inputProps={{ maxLength: 60, style: { fontSize: "0.88rem" } }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><Search sx={{ fontSize: 18, color: colors.textSecondary }} /></InputAdornment>) }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "22px", bgcolor: "#f1f3f5", "& fieldset": { border: "none" } } }}
          />
          <Box sx={{ mt: 1, maxHeight: 280, overflowY: "auto", border: "1px solid #e9ecef", borderRadius: "10px" }}>
            {visible.length === 0 && (
              <Typography sx={{ px: 2, py: 2, textAlign: "center", fontSize: "0.85rem", color: colors.textSecondary }}>Ninguém encontrado.</Typography>
            )}
            {visible.map((c) => {
              const checked = selected.includes(c.userId);
              return (
                <Box
                  key={c.userId}
                  onClick={() => toggle(c.userId)}
                  sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.25, py: 0.75, cursor: "pointer", "&:hover": { bgcolor: "#f6f7f9" } }}
                >
                  <Checkbox checked={checked} size="small" sx={{ p: 0.25, color: colors.primary, "&.Mui-checked": { color: colors.primary } }} inputProps={{ "aria-label": c.name }} />
                  <Avatar src={photos[c.userId]} sx={{ width: 34, height: 34, bgcolor: personColor(c.userId), color: "#fff", fontSize: "0.8rem", fontWeight: 600 }}>
                    {initials(c.name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: colors.textPrimary }} noWrap>{c.name}</Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary }} noWrap>{c.role ?? "Equipe"}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
        {error && <Typography sx={{ fontSize: "0.8rem", color: colors.error }}>{error}</Typography>}
        <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary }}>
          Você entra como administrador do grupo. Só quem participa lê a conversa; cada pessoa pode sair quando quiser.
        </Typography>
      </DialogContent>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", color: colors.textSecondary }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={create}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
          sx={{ textTransform: "none", bgcolor: colors.primary, "&:hover": { bgcolor: "#029AAB" } }}
        >
          Criar grupo
        </Button>
      </Box>
    </Dialog>
  );
};

export default NewGroupModal;
